<template>
  <ContentWrap title="本店自提点">
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      title="一店一点：每个店铺仅维护一个自提点。多店合单自提时，各店商品提到各自自提点。需平台将交易配置「自提模式」设为「一店一点」后生效。"
    />
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="120px"
      class="max-w-800px"
    >
      <el-form-item label="门店 logo" prop="logo">
        <UploadImg v-model="formData.logo" :limit="1" :is-show-tip="false" />
      </el-form-item>
      <el-form-item label="门店状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="门店名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入门店名称" />
      </el-form-item>
      <el-form-item label="门店手机" prop="phone">
        <el-input v-model="formData.phone" placeholder="请输入门店手机" />
      </el-form-item>
      <el-form-item label="门店简介" prop="introduction">
        <el-input v-model="formData.introduction" type="textarea" :rows="3" />
      </el-form-item>
      <el-form-item label="所在地区" prop="areaId">
        <el-cascader v-model="formData.areaId" :options="areaList" :props="defaultProps" />
      </el-form-item>
      <el-form-item label="详细地址" prop="detailAddress">
        <el-input v-model="formData.detailAddress" placeholder="请输入详细地址" />
      </el-form-item>
      <el-form-item label="营业时间">
        <el-time-select
          v-model="formData.openingTime"
          :max-time="formData.closingTime"
          class="!w-140px"
          placeholder="开始"
          start="08:30"
          step="00:15"
          end="23:30"
        />
        <span class="mx-8px">至</span>
        <el-time-select
          v-model="formData.closingTime"
          :min-time="formData.openingTime"
          class="!w-140px"
          placeholder="结束"
          start="08:30"
          step="00:15"
          end="23:30"
        />
      </el-form-item>
      <el-form-item label="经度" prop="longitude">
        <el-input v-model="formData.longitude" placeholder="经度" class="!w-200px" />
      </el-form-item>
      <el-form-item label="纬度" prop="latitude">
        <el-input v-model="formData.latitude" placeholder="纬度" class="!w-200px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="formLoading" @click="submitForm">保存</el-button>
        <el-button @click="loadData">刷新</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script lang="ts" setup>
import * as PickUpStoreApi from '@/api/merchant/delivery/pickUpStore'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { CommonStatusEnum } from '@/utils/constants'
import { defaultProps } from '@/utils/tree'
import * as AreaApi from '@/api/merchant/system/area'

defineOptions({ name: 'MerchantPickUpStore' })

const message = useMessage()
const formLoading = ref(false)
const formRef = ref()
const areaList = ref<any[]>([])
const formData = ref<PickUpStoreApi.MerchantPickUpStoreVO>({
  name: '',
  phone: '',
  logo: '',
  detailAddress: '',
  introduction: '',
  areaId: undefined,
  openingTime: undefined,
  closingTime: undefined,
  latitude: undefined,
  longitude: undefined,
  status: CommonStatusEnum.ENABLE
})
const formRules = reactive({
  name: [{ required: true, message: '门店名称不能为空', trigger: 'blur' }],
  logo: [{ required: true, message: '门店 logo 不能为空', trigger: 'blur' }],
  phone: [
    { required: true, message: '门店手机不能为空', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
  ],
  areaId: [{ required: true, message: '所在地区不能为空', trigger: 'change' }],
  detailAddress: [{ required: true, message: '详细地址不能为空', trigger: 'blur' }],
  openingTime: [{ required: true, message: '开始时间不能为空', trigger: 'change' }],
  closingTime: [{ required: true, message: '结束时间不能为空', trigger: 'change' }],
  latitude: [{ required: true, message: '纬度不能为空', trigger: 'blur' }],
  longitude: [{ required: true, message: '经度不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
})

const loadData = async () => {
  formLoading.value = true
  try {
    const data = await PickUpStoreApi.getMinePickUpStore()
    if (data) {
      formData.value = { ...formData.value, ...data }
    }
  } finally {
    formLoading.value = false
  }
}

const submitForm = async () => {
  try {
    await formRef.value.validate()
  } catch {
    return
  }
  formLoading.value = true
  try {
    await PickUpStoreApi.saveMinePickUpStore(formData.value)
    message.success('保存成功')
    await loadData()
  } finally {
    formLoading.value = false
  }
}

onMounted(async () => {
  try {
    // 经营台必须走 merchant-api 地区树，不能用管理端 /system/area
    areaList.value = (await AreaApi.getAreaTree()) || []
  } catch (e: any) {
    areaList.value = []
    message.error(e?.message || '地区数据加载失败')
  }
  await loadData()
})
</script>
